import React from "react";
import { useTranslation } from "react-i18next";

interface FileUploaderProps {
  /** Optional note to display below uploader */
  note?: string;
  /** Maximum file size in bytes */
  maxSize?: number;
  onFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  accept?: Array<string>;
  label?: string; // Appears below the icon for large variant
  description?: string; // Appears 20px below label for large variant
  variant?: "default" | "large";
  success?: boolean; // highlights with success styles
  loading?: boolean;
}

export default function FileUploader({
  onFileChange,
  disabled,
  accept,
  label,
  description,
  variant = "default",
  success = false,
  loading = false,
  note,
}: FileUploaderProps) {
  const { t } = useTranslation("common");
  const resolvedLabel = label ?? t("uploader.uploadFile");
  if (variant === "large") {
    return (
      <div
        className="flex flex-col gap-1.5"
        style={{ width: 400, height: 160 }}
      >
        <label
          className={[
            "w-full h-full border border-dashed cursor-pointer flex flex-col items-center justify-center px-5 py-5",
            success
              ? "border-green-600 bg-green-50 text-green-700 hover:bg-green-100"
              : "border-garnet bg-fog text-garnet hover:bg-fog-dark",
          ].join(" ")}
          style={{ minHeight: 160, position: "relative" }}
        >
          {/* Loader overlay */}
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
              <span className="loader border-2 border-garnet border-t-transparent rounded-full w-10 h-10 animate-spin" />
            </div>
          )}
          <img
            src="/icons/upload-icon.svg"
            className="h-[28px] w-[40px] mb-2"
            alt={t("uploader.uploadFileAlt")}
          />
          <div className="body-small text-center" style={{ maxWidth: 161 }}>
            {resolvedLabel}
          </div>
          {description && (
            <div className="body-small text-center mt-5">{description}</div>
          )}
          <input
            type="file"
            accept={accept?.join(",")}
            className="sr-only"
            onChange={onFileChange}
            disabled={disabled}
          />
        </label>
      </div>
    );
  }

  // Default variant (original)
  return (
    <div className="flex flex-col gap-1.5">
      <label
        className={[
          "w-full px-3 py-2.5 h-[50px] border border-dashed cursor-pointer flex items-center justify-between",
          success
            ? "border-green-600 bg-green-50 text-green-700 hover:bg-green-100"
            : "border-garnet bg-fog text-garnet hover:bg-fog-dark",
        ].join(" ")}
      >
        <span className="body text-[16px] w-full">{resolvedLabel}</span>
        <img
          src="/icons/upload-icon.svg"
          className="h-[18px] w-[26px]"
          alt="upload file"
        />
        <input
          type="file"
          accept={accept?.join(",")}
          className="sr-only"
          onChange={onFileChange}
          disabled={disabled}
        />
      </label>
      {note && (
        <div className="text-xs text-gray-500 mt-1 text-center w-full max-w-xs">
          {note}
        </div>
      )}
    </div>
  );
}
